import { AddContractData, AddContractSteps } from 'features/contracts/add/types'
import { store } from 'store'
import { defineProducts } from 'utils/products'
import { NormalizedAccount, NormalizedContact } from 'store/contact/types.ts'
import { selectCurrentBillingContract } from 'store/contact/slice.ts'
import {
  CONTACT_MODES,
  COUNTER_TYPES,
  EAN_CAPTURE_TYPES,
  Language,
  METER_TYPES,
  PAYMENT_METHODS,
  REGIONS,
  SETTLEMENT_FREQUENCIES,
  SIMULATION_TYPES,
  SITUATIONS
} from '@boltenergy-be/shared'
import { formatPhoneToE164 } from '@boltenergy-be/design-system'

export const GET_INITIAL_ADD_CONTRACT_DATA = (
  language: Language,
  currentAccount?: NormalizedAccount,
  contact?: NormalizedContact
): AddContractData => {
  // Constants
  const billingContract = selectCurrentBillingContract(store.getState())
  const contactMode = billingContract?.deliveryMode ?? CONTACT_MODES.email
  const elProduct = billingContract?.serviceContracts.electricity?.detail?.productCode || defineProducts(contactMode, false).electricity

  return {
    [AddContractSteps.ADDRESS]: {
      deliveryAddress: {
        countryCode: 'BE',
        postalCode: 0,
        townCode: 0,
        townName: '',
        streetName: '',
        streetNumber: '',
        streetBox: ''
      },
      needsGas: false,
      region: REGIONS.flanders
    },
    [AddContractSteps.PRODUCER]: undefined,
    [AddContractSteps.PRODUCT]: {
      electricity: elProduct,
      gas: undefined
    },
    [AddContractSteps.INSTALMENT_DATA]: {
      simulationType: SIMULATION_TYPES.eav,
      instalments: {
        electricity: 0
      }
    },
    [AddContractSteps.METER_DETAILS]: {
      contractStartDate: null,
      counterType: COUNTER_TYPES.digital,
      eanCaptureType: EAN_CAPTURE_TYPES.noLookupDone,
      electricity: {
        ean: ''
      },
      hasExclNight: false,
      hasExclNightEan: false,
      hasSolarPanels: false,
      meterReadingMonth: null,
      meterType: METER_TYPES.double,
      situation: SITUATIONS.switch,
      dynamicTariff: false,
      settlementFrequency: SETTLEMENT_FREQUENCIES.yearly
    },
    [AddContractSteps.CUSTOMER_DATA]: {
      customerNumber: currentAccount?.customerNumber,
      isCompany: !!currentAccount?.company?.enterpriseNumber || false,
      company: currentAccount?.company,
      firstName: currentAccount?.firstName || '',
      lastName: currentAccount?.lastName || '',
      email: contact?.email || '',
      emailConfirmation: contact?.email || '',
      phoneMobile: contact?.phone ? formatPhoneToE164(contact.phone) : '',
      birthDate: currentAccount?.birthDate,
      language,
      sameCorrespondenceAddress: true,
      correspondenceAddress: currentAccount?.correspondenceAddress
        ? {
            ...currentAccount.correspondenceAddress,
            townCode: 0,
            countryCode: 'BE'
          }
        : undefined,
      contactMode,
      paymentMethod: currentAccount?.paymentDetails?.directDebit ? PAYMENT_METHODS.domiciliation : PAYMENT_METHODS.transfer,
      iban: currentAccount?.paymentDetails?.iban || undefined
    }
  } satisfies AddContractData
}

export const ADD_CONTRACT_FLOW = [
  AddContractSteps.ADDRESS,
  AddContractSteps.PRODUCER,
  AddContractSteps.PRODUCT,
  AddContractSteps.INSTALMENT_DATA,
  AddContractSteps.METER_DETAILS,
  AddContractSteps.CUSTOMER_DATA,
  AddContractSteps.CONFIRMATION,
  AddContractSteps.DONE
]

export const MOVE_CONTRACT_FLOW = [
  AddContractSteps.ADDRESS,
  AddContractSteps.REFERRAL,
  AddContractSteps.PRODUCER,
  AddContractSteps.PRODUCT,
  AddContractSteps.INSTALMENT_DATA,
  AddContractSteps.METER_DETAILS,
  AddContractSteps.CUSTOMER_DATA,
  AddContractSteps.CONFIRMATION,
  AddContractSteps.DONE
]

export const MIN_AGE = 18
